import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { WorkspaceProvider, useWorkspace } from "./WorkspaceContext";
import { Login, Register } from "./Auth";
import Landing from "./Landing";
import Onboarding from "./Onboarding";
import Sidebar from "./components/Sidebar";
import CmdPalette from "./components/CmdPalette";
import Dashboard from "./views/Dashboard";
import Board from "./views/Board";
import Detail from "./views/Detail";
import Tasks from "./views/Tasks";
import Scholarships from "./views/Scholarships";
import Profile from "./views/Profile";
import Autofill from "./views/Autofill";
import Admin from "./views/Admin";
import Billing from "./views/Billing";
import { PrivacyPolicy, TermsAndConditions, ExtensionPrivacy } from "./Legal";
import OAuthAuthorize from "./OAuthAuthorize";
import { UpgradeProvider } from "./UpgradeContext";
import { CopilotProvider, useCopilot } from "./CopilotContext";
import CopilotDrawer from "./components/CopilotDrawer";
import "./app2.css";

// Subroutes within /app that map to views. Anything not listed (or `/app` itself)
// falls through to the Home (dashboard) view.
const PATH_TO_VIEW = {
  "/app": "dashboard",
  "/app/dashboard": "dashboard",
  "/app/chat": "chat",
  "/app/board": "board",
  "/app/tasks": "tasks",
  "/app/scholarships": "scholarships",
  "/app/profile": "profile",
  "/app/autofill": "autofill",
  "/app/admin": "admin",
  "/app/billing": "billing",
};
const VIEW_TO_PATH = {
  dashboard: "/app/dashboard",
  chat: "/app/chat",
  board: "/app/board",
  tasks: "/app/tasks",
  scholarships: "/app/scholarships",
  profile: "/app/profile",
  autofill: "/app/autofill",
  admin: "/app/admin",
  billing: "/app/billing",
};

function Workspace() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openCopilot } = useCopilot();
  const { universities, loaded } = useWorkspace();
  const initialView = PATH_TO_VIEW[location.pathname] || "dashboard";
  const [view, setViewState] = useState(initialView);
  const [detailUni, setDetailUni] = useState(null);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Keep view in sync when the URL changes (back/forward, direct URL, sidebar nav).
  // Detail is now a real /app/university/:id route so the history stack is honest.
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/app/university/")) {
      const id = path.slice("/app/university/".length);
      if (id) { setViewState("detail"); setDetailUni(id); }
      return;
    }
    const v = PATH_TO_VIEW[path];
    if (v === "chat") { openCopilot("menu"); navigate("/app/dashboard", { replace: true }); return; }
    if (v) setViewState(v);
  }, [location.pathname]); // eslint-disable-line

  // Close mobile drawer when route changes
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Guard: only admins can hit /app/admin directly; others bounce to /app
  useEffect(() => {
    if (location.pathname === "/app/admin" && user && user.role !== "admin") {
      navigate("/app", { replace: true });
    }
  }, [location.pathname, user, navigate]);

  // Post-onboarding "aha": once the workspace loads, open the focus uni's fit report.
  useEffect(() => {
    if (!loaded) return;
    const focus = sessionStorage.getItem("vidhya_onboard_focus");
    if (!focus) return;
    sessionStorage.removeItem("vidhya_onboard_focus");
    const u = (universities || []).find((x) => x.id === focus) || (universities || [])[0];
    if (u) { navigate(`/app/university/${u.id}`); }
  }, [loaded]); // eslint-disable-line

  const go = (v, payload) => {
    setSidebarOpen(false);
    // "chat" now opens the slide-in Ask Vidhya copilot instead of a full page.
    if (v === "chat") {
      openCopilot(
        payload && payload.mode ? payload.mode : "menu",
        payload && payload.ctx ? payload.ctx : null,
        payload && payload.options ? payload.options : {},
      );
      return;
    }
    if (v === "detail" && payload) {
      // Detail is a real, history-pushable route now so the browser/system back
      // button returns to the board instead of jumping out of the drill-down.
      navigate(`/app/university/${payload}`);
      return;
    }
    setViewState(v);
    const target = VIEW_TO_PATH[v];
    if (target && location.pathname !== target) {
      navigate(target);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
      if (e.key === "Escape") setSidebarOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="app app2" data-testid="workspace">
      <div
        className={`sidebar-overlay ${sidebarOpen ? "open" : ""}`}
        onClick={() => setSidebarOpen(false)}
        data-testid="sidebar-overlay"
      />
      <Sidebar
        view={view}
        go={go}
        openCmd={() => setCmdOpen(true)}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div className="main">
        <button className="vmobile-menu" onClick={() => setSidebarOpen(true)} aria-label="Open menu" data-testid="mobile-menu-btn">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
        </button>
        <div className="content">
          {view === "dashboard" && <Dashboard go={go} />}
          {view === "board" && <Board openDetail={(id) => go("detail", id)} />}
          {view === "detail" && <Detail uniId={detailUni} back={() => { if (window.history.length > 1) navigate(-1); else navigate("/app/board"); }} />}
          {view === "tasks" && <Tasks go={go} />}
          {view === "scholarships" && <Scholarships go={go} />}
          {view === "profile" && <Profile />}
          {view === "autofill" && <Autofill />}
          {view === "admin" && <Admin />}
          {view === "billing" && <Billing />}
        </div>
      </div>
      <CmdPalette open={cmdOpen} onClose={() => setCmdOpen(false)} go={go} />
      <CopilotDrawer go={go} />
    </div>
  );
}

function Protected({ children }) {
  const { status, user } = useAuth();
  if (status === "loading") return <div className="full-loader">Loading…</div>;
  if (status === "guest") return <Navigate to="/" replace />;
  if (user && user.onboarded === false) return <Navigate to="/onboarding" replace />;
  return (
    <WorkspaceProvider>
      <UpgradeProvider>
        <CopilotProvider>{children}</CopilotProvider>
      </UpgradeProvider>
    </WorkspaceProvider>
  );
}

function OnboardingRoute() {
  const { status, user } = useAuth();
  if (status === "loading") return <div className="full-loader">Loading…</div>;
  if (status === "guest") return <Navigate to="/login" replace />;
  if (user && user.onboarded === true) return <Navigate to="/app" replace />;
  return <Onboarding />;
}

function GuestOnly({ children }) {
  const { status, user } = useAuth();
  if (status === "loading") return <div className="full-loader">Loading…</div>;
  if (status === "authed") {
    if (user && user.onboarded === false) return <Navigate to="/onboarding" replace />;
    return <Navigate to="/app" replace />;
  }
  return children;
}

function PublicLanding() {
  const { status, user } = useAuth();
  if (status === "loading") return <div className="full-loader">Loading…</div>;
  if (status === "authed") {
    if (user && user.onboarded === false) return <Navigate to="/onboarding" replace />;
    return <Navigate to="/app" replace />;
  }
  return <Landing />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<PublicLanding />} />
      <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
      <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
      <Route path="/onboarding" element={<OnboardingRoute />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/terms-and-conditions" element={<Navigate to="/terms" replace />} />
      <Route path="/extension-privacy" element={<ExtensionPrivacy />} />
      <Route path="/extension/privacy" element={<Navigate to="/extension-privacy" replace />} />
      <Route path="/oauth/authorize" element={<OAuthAuthorize />} />
      <Route path="/app/*" element={<Protected><Workspace /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
